import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Phone,
  MessageSquare,
  Clock,
  ExternalLink,
  ArrowLeft,
  Timer,
  CalendarClock,
  CircleCheck,
  Search,
} from "lucide-react";
import Seo from "@/components/site/Seo";
import { BrandMark } from "@/components/site/BrandMark";
import VerificationSeal from "@/components/site/VerificationSeal";
import ScamWarning from "@/components/site/ScamWarning";
import SpecialistCTA from "@/components/site/SpecialistCTA";
import BrandRequestContact from "@/components/site/BrandRequestContact";
import BrandIssues from "@/components/site/BrandIssues";
import BrandGuide from "@/components/site/BrandGuide";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { useBrand } from "@/lib/brands";
import { computeHours, getVisitorTz, formatVerifiedDate } from "@/lib/brandHours";

export default function BrandPage() {
  const { slug } = useParams();
  const { data: brand, isLoading, isError } = useBrand(slug);
  const [issueQuery, setIssueQuery] = useState("");

  if (isLoading) {
    return (
      <div className="py-24 text-center font-mono text-sm text-muted">Loading…</div>
    );
  }
  if (isError || !brand) {
    return (
      <div className="py-24 text-center">
        <h1 className="font-serif text-3xl text-ink">Brand not found</h1>
        <p className="mt-3 text-muted">We don&apos;t have that one yet.</p>
        <Link to="/" className="mt-6 inline-block border-b border-ink font-mono text-xs uppercase tracking-widest">
          ← Back to search
        </Link>
      </div>
    );
  }

  const hours = computeHours(brand.hours);
  const visitorTz = getVisitorTz();
  const telHref = brand.phone ? `tel:${brand.phone.replace(/[^+\d]/g, "")}` : null;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: brand.name,
      description: brand.blurb || `${brand.name} customer service contact information.`,
      ...(brand.source_url ? { url: brand.source_url } : {}),
      ...(brand.phone
        ? {
            contactPoint: {
              "@type": "ContactPoint",
              telephone: brand.phone,
              contactType: "customer service",
            },
          }
        : {}),
    },
    ...(brand.common_issues?.length
      ? [
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: brand.common_issues.map((it) => ({
              "@type": "Question",
              name: `How do I fix "${it.problem}" with ${brand.name}?`,
              acceptedAnswer: { "@type": "Answer", text: it.solution },
            })),
          },
        ]
      : []),
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${window.location.origin}/` },
        { "@type": "ListItem", position: 2, name: brand.category, item: `${window.location.origin}/category/${brand.category}` },
        { "@type": "ListItem", position: 3, name: brand.name, item: `${window.location.origin}/brand/${brand.slug}` },
      ],
    },
  ];

  return (
    <div className="pb-16">
      <Seo
        title={`${brand.name} Customer Service Number & Support — It's Genie Assist`}
        description={`Verified official ${brand.name} customer service phone number, chat link and support hours. Independent directory — not affiliated with ${brand.name}.`}
        jsonLd={jsonLd}
      />

      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 py-5 font-mono text-[11px] uppercase tracking-widest text-muted" aria-label="Breadcrumb">
        <Link to="/" className="inline-flex items-center gap-1 hover:text-accent">
          <ArrowLeft className="h-3 w-3" /> Home
        </Link>
        <span>/</span>
        <Link to={`/category/${brand.category}`} className="hover:text-accent">
          {brand.category}
        </Link>
        <span>/</span>
        <span className="text-ink">{brand.name}</span>
      </nav>

      {/* Header row */}
      <div className="flex items-start gap-4 border-b border-hair pb-6">
        <BrandMark name={brand.name} color={brand.color} size="lg" />
        <div>
          <h1 className="font-serif text-4xl font-medium tracking-tight text-ink sm:text-5xl">
            {brand.name}
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted">
            {brand.blurb}{" "}
            <span className="text-ink">
              We are independent and not affiliated with {brand.name}.
            </span>
          </p>
        </div>
      </div>

      {/* ABOVE THE FOLD: contact + verification */}
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="grid gap-px border-b border-hair bg-hair md:grid-cols-[1.4fr_1fr]"
        data-testid="brand-contact-block"
      >
        {/* Left: phone + chat */}
        <div className="bg-paper p-6 sm:p-8">
          <div className="font-mono text-[11px] uppercase tracking-widest text-muted">
            Official phone number
          </div>
          {brand.phone ? (
            <a
              href={telHref}
              data-testid="brand-phone-link"
              className="mt-3 block font-mono text-4xl font-semibold tracking-tighter text-ink underline-offset-4 hover:text-accent hover:underline sm:text-5xl md:text-6xl"
            >
              {brand.phone}
            </a>
          ) : (
            <p className="mt-3 font-serif text-2xl text-ink">
              No public phone line
            </p>
          )}

          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            {brand.phone && (
              <a
                href={telHref}
                data-testid="brand-call-button"
                className="inline-flex items-center justify-center gap-2 bg-accent px-6 py-3.5 font-mono text-sm uppercase tracking-widest text-paper transition-opacity hover:opacity-90"
              >
                <Phone className="h-4 w-4" /> Tap to call
              </a>
            )}
            {brand.chat_url && (
              <a
                href={brand.chat_url}
                target="_blank"
                rel="noopener noreferrer nofollow"
                data-testid="brand-chat-link"
                className="inline-flex items-center justify-center gap-2 border-2 border-ink px-6 py-3.5 font-mono text-sm uppercase tracking-widest text-ink transition-colors hover:border-accent hover:text-accent"
              >
                <MessageSquare className="h-4 w-4" /> Official chat / help
              </a>
            )}
          </div>

          {/* Hours */}
          <div className="mt-8 border-t border-hair pt-5">
            <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-muted">
              <Clock className="h-4 w-4" /> Support hours
            </div>
            <div className="mt-2 flex items-center gap-2">
              <span
                className={`inline-flex items-center gap-1.5 font-mono text-sm ${
                  hours.open ? "text-accent" : "text-muted"
                }`}
                data-testid="brand-hours-status"
              >
                <CircleCheck className="h-4 w-4" /> {hours.label}
              </span>
            </div>
            {hours.todayWindow && (
              <p className="mt-1 text-sm text-ink">
                Today: <span className="font-mono">{hours.todayWindow}</span>
              </p>
            )}
            <p className="mt-1 font-mono text-[11px] text-muted">
              {brand.hours?.note} · Shown in your timezone ({visitorTz})
            </p>
          </div>
        </div>

        {/* Right: verification seal + source */}
        <div className="flex flex-col items-center justify-center gap-5 bg-paper p-6 text-center sm:p-8">
          <VerificationSeal date={brand.verified_on} />
          <div>
            <p className="font-mono text-[11px] uppercase tracking-widest text-muted">
              Verified on {formatVerifiedDate(brand.verified_on)}
            </p>
            {brand.source_url && (
              <a
                href={brand.source_url}
                target="_blank"
                rel="noopener noreferrer nofollow"
                data-testid="brand-source-link"
                className="mt-2 inline-flex items-center gap-1.5 border-b border-ink font-mono text-xs text-ink hover:border-accent hover:text-accent"
              >
                View official source <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
          </div>
        </div>
      </motion.section>

      {/* Stats row */}
      <section className="grid gap-px border-b border-hair bg-hair sm:grid-cols-2">
        <div className="bg-paper p-6">
          <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-muted">
            <Timer className="h-4 w-4" /> Average wait
          </div>
          <p className="mt-2 font-serif text-2xl text-ink">{brand.avg_wait || "—"}</p>
        </div>
        <div className="bg-paper p-6">
          <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-muted">
            <CalendarClock className="h-4 w-4" /> Best time to call
          </div>
          <p className="mt-2 font-serif text-2xl text-ink">{brand.best_time || "—"}</p>
        </div>
      </section>

      {/* Brand-specific written guide (unique content per page) */}
      <BrandGuide
        brand={brand}
        hoursLabel={brand.hours?.is_24_7 ? "24/7" : (brand.hours?.note || "during published hours")}
      />

      {/* Common issues + solutions */}
      {brand.common_issues?.length > 0 &&
        (() => {
          const q = issueQuery.trim().toLowerCase();
          const issues = brand.common_issues;
          const filtered = q
            ? issues.filter(
                (it) =>
                  it.problem.toLowerCase().includes(q) ||
                  it.solution.toLowerCase().includes(q)
              )
            : issues;
          const renderItem = (it, i) => (
            <AccordionItem
              key={i}
              value={`issue-${i}`}
              className="border-b border-hair"
              data-testid={`issue-${i}`}
            >
              <AccordionTrigger className="gap-4 py-5 hover:no-underline [&[data-state=open]>svg]:text-accent">
                <span className="flex items-start gap-3 text-left">
                  <span className="font-mono text-sm text-accent">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-serif text-lg font-medium text-ink">{it.problem}</span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="pl-9">
                <p className="max-w-2xl text-sm leading-relaxed text-muted">{it.solution}</p>
              </AccordionContent>
            </AccordionItem>
          );
          return (
            <section className="border-b border-hair py-8" data-testid="common-issues">
              <h2 className="font-serif text-2xl font-medium text-ink">
                Common problems &amp; how to fix them
              </h2>
              <p className="mt-2 max-w-2xl text-sm text-muted">
                Search your exact issue, or browse below — most are solved without waiting on hold.
              </p>

              <div className="mt-5 flex items-center gap-3 border-2 border-hair px-4 py-3 transition-colors focus-within:border-accent">
                <Search className="h-4 w-4 text-muted" strokeWidth={1.75} />
                <input
                  data-testid="issue-search-input"
                  value={issueQuery}
                  onChange={(e) => setIssueQuery(e.target.value)}
                  placeholder={`Search ${brand.name} problems — e.g. refund, login, hacked`}
                  className="w-full bg-transparent font-body text-sm text-ink placeholder:text-muted/70 focus:outline-none"
                  aria-label={`Search ${brand.name} problems`}
                />
                {q && (
                  <button
                    type="button"
                    data-testid="issue-search-clear"
                    onClick={() => setIssueQuery("")}
                    className="font-mono text-[11px] uppercase tracking-widest text-muted hover:text-accent"
                  >
                    Clear
                  </button>
                )}
              </div>

              {filtered.length === 0 ? (
                <div
                  data-testid="issue-no-match"
                  className="mt-5 border border-hair p-6 text-sm text-muted"
                >
                  No matching problem for &ldquo;{issueQuery}&rdquo;. Try a different word, call the
                  number above, or{" "}
                  <Link to="/contact" className="text-accent underline">
                    ask us to add it
                  </Link>
                  .
                </div>
              ) : q ? (
                <Accordion
                  type="multiple"
                  value={filtered.map((_, i) => `issue-${i}`)}
                  className="mt-5 border-t border-hair"
                >
                  {filtered.map(renderItem)}
                </Accordion>
              ) : (
                <Accordion type="single" collapsible className="mt-5 border-t border-hair">
                  {filtered.map(renderItem)}
                </Accordion>
              )}
            </section>
          );
        })()}

      {/* Customer-reported issues (public list + submit) */}
      <BrandIssues brand={brand} />

      {/* Request contact with this brand */}
      <BrandRequestContact brand={brand} />

      {/* Optional paid concierge — clearly separate from the free contacts */}
      <section className="border-t border-hair py-8" data-testid="brand-specialist">
        <SpecialistCTA brand={brand} />
      </section>

      {/* Scam warning */}
      <section className="py-8">
        <ScamWarning />
      </section>
    </div>
  );
}
